import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc,
  deleteDoc,
  query, 
  orderBy, 
  limit,
  getDocs,
  onSnapshot,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get the chat messages collection reference for a user
 */
const getMessagesCollection = (userId) => {
  return collection(db, 'users', userId, 'messages');
};

/**
 * Convert Firestore timestamp to Date
 */
const convertTimestamp = (timestamp) => {
  if (!timestamp) return new Date();
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

/**
 * Convert message to Firestore format
 * Note: illustration field is not stored (images are local-only)
 */
const messageToFirestore = (message) => {
  return {
    text: message.text,
    isBot: message.isBot || false,
    question: message.question || null,
    isParentUpdate: message.isParentUpdate || false,
    replyToQuestionId: message.replyToQuestionId || null,
    // illustration field is intentionally omitted - images are not stored
    timestamp: message.timestamp ? Timestamp.fromDate(new Date(message.timestamp)) : serverTimestamp()
  };
};

/**
 * Convert Firestore document to message format
 * Note: illustration field is ignored (images are local-only, not stored)
 */
const firestoreToMessage = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    text: data.text || '',
    isBot: data.isBot || false,
    question: data.question || null,
    isParentUpdate: data.isParentUpdate || false,
    replyToQuestionId: data.replyToQuestionId || null,
    // illustration field is intentionally omitted - images are not stored
    timestamp: convertTimestamp(data.timestamp)
  };
};

/**
 * Load messages from Firestore
 */
export const loadMessages = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const messagesRef = getMessagesCollection(userId);
  const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));
  
  return new Promise((resolve, reject) => {
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map(firestoreToMessage);
        // If no messages, return default initial message
        if (messages.length === 0) {
          resolve([{
            id: 'default',
            text: "Hi, I'm Dadbot, and I can help answer questions the way Dad would.",
            isBot: true,
            timestamp: new Date()
          }]);
        } else {
          resolve(messages);
        }
      },
      (error) => {
        console.error('Error loading messages:', error);
        reject(error);
      }
    );
    
    // Return unsubscribe function
    return unsubscribe;
  });
};

/**
 * Subscribe to real-time message updates
 */
export const subscribeToMessages = (userId, callback) => {
  if (!userId) {
    console.warn('User ID is required for message subscription');
    // Still call callback with default message
    callback([{
      id: 'default',
      text: "Hi, I'm Dadbot, and I can help answer questions the way Dad would.",
      isBot: true,
      timestamp: new Date()
    }]);
    return () => {};
  }

  try {
    const messagesRef = getMessagesCollection(userId);
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));
    
    return onSnapshot(
      q,
      (snapshot) => {
        try {
          const messages = snapshot.docs.map(firestoreToMessage);
          // If no messages, return default initial message
          if (messages.length === 0) {
            callback([{
              id: 'default',
              text: "Hi, I'm Dadbot, and I can help answer questions the way Dad would.",
              isBot: true,
              timestamp: new Date()
            }]);
          } else {
            callback(messages);
          }
        } catch (parseError) {
          console.error('Error parsing messages:', parseError);
          // Return default message on parse error
          callback([{
            id: 'default',
            text: "Hi, I'm Dadbot, and I can help answer questions the way Dad would.",
            isBot: true,
            timestamp: new Date()
          }]);
        }
      },
      (error) => {
        console.error('Error subscribing to messages:', error);
        // Always call callback with default message on error
        callback([{
          id: 'default',
          text: "Hi, I'm Dadbot, and I can help answer questions the way Dad would.",
          isBot: true,
          timestamp: new Date()
        }]);
      }
    );
  } catch (error) {
    console.error('Error setting up message subscription:', error);
    // Call callback immediately with default message if subscription setup fails
    callback([{
      id: 'default',
      text: "Hi, I'm Dadbot, and I can help answer questions the way Dad would.",
      isBot: true,
      timestamp: new Date()
    }]);
    return () => {};
  }
};

/**
 * Add a new message to Firestore
 */
export const addMessage = async (userId, message) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const messagesRef = getMessagesCollection(userId);
  const messageData = messageToFirestore(message);
  
  try {
    const docRef = await addDoc(messagesRef, messageData);
    return {
      id: docRef.id,
      ...message,
      timestamp: message.timestamp || new Date()
    };
  } catch (error) {
    console.error('Error adding message:', error);
    throw error;
  }
};

/**
 * Update an existing message
 * Note: illustration updates are ignored (images are local-only, not stored)
 */
export const updateMessage = async (userId, messageId, updates) => {
  if (!userId || !messageId) {
    throw new Error('User ID and message ID are required');
  }

  const messageRef = doc(db, 'users', userId, 'messages', messageId);
  
  try {
    const updateData = {};
    // illustration field is intentionally ignored - images are not stored
    if (updates.text !== undefined) {
      updateData.text = updates.text;
    }
    
    await updateDoc(messageRef, updateData);
  } catch (error) {
    console.error('Error updating message:', error);
    throw error;
  }
};

/**
 * Check if messages exist in Firestore (one-time read)
 */
const checkMessagesExist = async (userId) => {
  const messagesRef = getMessagesCollection(userId);
  const q = query(messagesRef, limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

/**
 * Add a parent update message to Firestore
 * This creates a message from parentclone that appears as a reply to the original question
 */
export const addParentUpdate = async (userId, updateText, replyToQuestionId, originalQuestionText) => {
  if (!userId || !updateText || !replyToQuestionId) {
    throw new Error('User ID, update text, and reply to question ID are required');
  }

  const parentUpdateMessage = {
    text: updateText,
    isBot: true, // Appears as bot message (from parentclone)
    isParentUpdate: true,
    replyToQuestionId: replyToQuestionId,
    question: originalQuestionText, // Store original question text for display
    timestamp: new Date()
  };

  return addMessage(userId, parentUpdateMessage);
};

/**
 * Delete a single message from Firestore
 */
export const deleteMessage = async (userId, messageId) => {
  if (!userId || !messageId) {
    throw new Error('User ID and message ID are required');
  }

  const messageRef = doc(db, 'users', userId, 'messages', messageId);
  
  try {
    await deleteDoc(messageRef);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Delete an entire thread (question, answer, and all parent updates)
 */
export const deleteThread = async (userId, questionId, answerId, parentUpdateIds = []) => {
  if (!userId || !questionId || !answerId) {
    throw new Error('User ID, question ID, and answer ID are required');
  }

  try {
    // Delete the question
    await deleteMessage(userId, questionId);
    
    // Delete the answer
    await deleteMessage(userId, answerId);
    
    // Delete all parent updates
    for (const updateId of parentUpdateIds) {
      await deleteMessage(userId, updateId);
    }
  } catch (error) {
    console.error('Error deleting thread:', error);
    throw error;
  }
};

/**
 * Migrate messages from localStorage to Firestore
 */
export const migrateMessagesFromLocalStorage = async (userId) => {
  if (!userId) {
    return;
  }

  try {
    // Check if messages already exist in Firestore
    const messagesExist = await checkMessagesExist(userId);
    if (messagesExist) {
      // Messages already exist, don't migrate
      console.log('Messages already exist in Firestore, skipping migration');
      return;
    }

    const savedMessages = localStorage.getItem('dadbot_messages');
    if (!savedMessages) {
      return;
    }

    const parsed = JSON.parse(savedMessages);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return;
    }

    const messagesRef = getMessagesCollection(userId);
    
    // Migrate messages
    console.log(`Migrating ${parsed.length} messages from localStorage to Firestore`);
    for (const msg of parsed) {
      try {
        await addDoc(messagesRef, messageToFirestore({
          ...msg,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
        }));
      } catch (error) {
        console.error('Error migrating message:', error);
      }
    }

    // Clear localStorage after successful migration
    localStorage.removeItem('dadbot_messages');
    console.log('Migration completed successfully');
  } catch (error) {
    console.error('Error migrating messages from localStorage:', error);
  }
};

